import React from "react"
import * as Sentry from "@sentry/browser"

// Provider
import ThemeContext, { ThemeProvider } from "./src/context/ThemeContext"

// Components
import ErrorBoundary from "./src/components/ErrorBoundary/ErrorBoundaryContainer"
import Layout from "./src/components/Layout/Layout"
import Loader from "./src/components/Loader/Loader"

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    tracesSampleRate: 1.0,
    beforeSend(event, hint) {
      const message = event.message || ""
      const url = event.request?.url || ""
      const culprit = event.culprit || ""

      const stackFilenames =
        event.exception?.values?.flatMap(v => v.stacktrace?.frames?.map(f => f.filename || "") || []) || []

      const ignoredSources = [
        "chrome-extension://",
        "moz-extension://",
        "safari-web-extension://",
        "ct.pinterest.com",
        "facebook.net",
        "google-analytics.com",
        "cdn.shopify.com",
      ]

      const ignoredMessages = [
        "NetworkError when attempting to fetch resource",
        "ResizeObserver loop limit exceeded",
        "Non-Error exception captured",
        "Script error.",
        "Non-Error promise rejection captured with value: Object Not Found Matching Id",
      ]

      const isGbViewError =
        message.includes("gbView") ||
        event.exception?.values?.some(exception =>
          exception.stacktrace?.frames?.some(frame => frame.function?.includes("gbView")),
        )

      const shouldIgnoreSource = ignoredSources.some(
        src =>
          url.includes(src) ||
          culprit.includes(src) ||
          message.includes(src) ||
          stackFilenames.some(filename => filename.includes(src)),
      )

      if (shouldIgnoreSource || ignoredMessages.some(msg => message.includes(msg)) || isGbViewError) {
        return null
      }

      return event
    },
  })

  window.Sentry = Sentry
}

export const onClientEntry = () => {
  if (typeof window === "undefined") return

  const cookies = document.cookie.split("; ").reduce((acc, item) => {
    const [key, value] = item.split("=")
    acc[key] = value
    return acc
  }, {})

  if (cookies.storeOverride === "true") {
    // User has overridden detection — don’t auto-detect
    return
  }

  const country = cookies.country
    ? ["US", "CA", "GB"].includes(cookies.country)
      ? cookies.country
      : "worldwide"
    : "US"
  const shipping = ["US", "CA", "GB"].includes(country)

  window.localStorage.setItem("country", country)
  window.localStorage.setItem("shipping", shipping.toString())
}

const Wrapper = ({ element, props }) => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ThemeContext.Consumer>
          {theme => (
            <React.Fragment>
              {theme.pageLoader && <Loader isLoader={theme.pageLoader} />}
              {element && <Layout {...props}>{element}</Layout>}
            </React.Fragment>
          )}
        </ThemeContext.Consumer>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export const wrapPageElement = Wrapper
